import { LECTERN } from '@vrlearning/lesson-schema';

/**
 * The test drive's panel (ADR-048, spec §3), as data: which view is shown with ◀ ▶ to the others, a
 * "Terug" to the first step, and a button per animation (a step `anim-<name>` of the test lesson), three
 * to a row, three rows to a page and a pager row under them when there are more. Positions in metres on
 * the panel, centred, rows going down. Drawn by render/test-panel.ts; tested here, where it can be.
 */
export type TestPanelAction =
  | { kind: 'view'; index: number }
  | { kind: 'step'; id: string }
  | { kind: 'page'; page: number }
  | { kind: 'none' };
export type TestPanelButton = {
  label: string;
  action: TestPanelAction;
  x: number;
  y: number;
  width: number;
};

/** Buttons 12 cm high at the lectern's 1.9 m: 3.6°, as the lectern's own small buttons are. */
export const TEST_PANEL = {
  width: 0.9,
  rowHeight: 0.12,
  gap: 0.02,
  columns: 3,
  /** Rows of animations on a page; with the view row and a pager row, five rows: under the lectern's 0.78 m. */
  gridRows: 3,
} as const;

/** The first step: "Terug" goes back to looking at the item. */
export const LOOK_STEP = 'bekijken';

export type TestPanelInput = {
  views: readonly { id: string; label: string }[];
  current: number;
  steps: readonly { id: string; title: string }[];
  /** Which page of animations (0-based); clamped. */
  page?: number;
};

/** How many pages of animation buttons these steps make. */
export function testPanelPages(steps: TestPanelInput['steps']): number {
  const slots = slotsOf(steps).length;
  return Math.max(1, Math.ceil(slots / (TEST_PANEL.columns * TEST_PANEL.gridRows)));
}

function slotsOf(steps: TestPanelInput['steps']): { label: string; id: string }[] {
  const slots: { label: string; id: string }[] = [];
  if (steps.some((step) => step.id === LOOK_STEP)) slots.push({ label: 'Terug', id: LOOK_STEP });
  for (const step of steps) {
    if (step.id.startsWith('anim-')) slots.push({ label: step.title, id: step.id });
  }
  return slots;
}

/** How many rows the panel is tall for these steps: the view row, the page's animation rows, a pager row. */
export function testPanelRows(steps: TestPanelInput['steps'], page = 0): number {
  const slots = slotsOf(steps);
  const size = TEST_PANEL.columns * TEST_PANEL.gridRows;
  const shown = slots.slice(page * size, (page + 1) * size).length;
  return 1 + Math.ceil(shown / TEST_PANEL.columns) + (testPanelPages(steps) > 1 ? 1 : 0);
}

export function testPanelHeight(rows: number): number {
  return rows * TEST_PANEL.rowHeight + (rows - 1) * TEST_PANEL.gap;
}

/** Where the panel's first row is, in the lectern's own plane, when it hangs on `side` (1 right, −1 left). */
export function testPanelAt(side: 1 | -1 = 1, aside = 0): { x: number; y: number } {
  return {
    x: side * (LECTERN.width / 2 + 0.05 + TEST_PANEL.width / 2 + aside),
    y: LECTERN.panelY + LECTERN.height / 2 - TEST_PANEL.rowHeight / 2,
  };
}

export function testPanelButtons(input: TestPanelInput): TestPanelButton[] {
  const { width, rowHeight, gap, columns, gridRows } = TEST_PANEL;
  const cell = (width - gap * (columns - 1)) / columns;
  const count = input.views.length;
  const buttons: TestPanelButton[] = [];
  const top = 0;
  const here = input.views[input.current]!;
  if (count > 1) {
    buttons.push(
      {
        label: '◀',
        action: { kind: 'view', index: (input.current - 1 + count) % count },
        x: -(cell + gap),
        y: top,
        width: cell,
      },
      { label: here.label, action: { kind: 'none' }, x: 0, y: top, width: cell },
      {
        label: '▶',
        action: { kind: 'view', index: (input.current + 1) % count },
        x: cell + gap,
        y: top,
        width: cell,
      },
    );
  } else {
    buttons.push({ label: here.label, action: { kind: 'none' }, x: 0, y: top, width });
  }
  const pages = testPanelPages(input.steps);
  const page = Math.min(Math.max(input.page ?? 0, 0), pages - 1);
  const size = columns * gridRows;
  slotsOf(input.steps)
    .slice(page * size, (page + 1) * size)
    .forEach((slot, index) => {
      const row = Math.floor(index / columns) + 1;
      buttons.push({
        label: slot.label,
        action: { kind: 'step', id: slot.id },
        x: ((index % columns) - 1) * (cell + gap),
        y: top - row * (rowHeight + gap),
        width: cell,
      });
    });
  if (pages > 1) {
    const rows = testPanelRows(input.steps, page);
    const y = top - (rows - 1) * (rowHeight + gap);
    buttons.push(
      {
        label: '◀',
        action: { kind: 'page', page: (page - 1 + pages) % pages },
        x: -(cell + gap),
        y,
        width: cell,
      },
      { label: `${page + 1} / ${pages}`, action: { kind: 'none' }, x: 0, y, width: cell },
      {
        label: '▶',
        action: { kind: 'page', page: (page + 1) % pages },
        x: cell + gap,
        y,
        width: cell,
      },
    );
  }
  return buttons;
}

/**
 * Pressing a step's button: go there; the step already shown is played again (through the first step,
 * as the lectern's own "↻ Nog eens" starts it over).
 */
export function pressStep(
  player: { state: { step: { id: string } }; goto(stepId: string): void },
  id: string,
): void {
  if (player.state.step.id === id && id !== LOOK_STEP) player.goto(LOOK_STEP);
  player.goto(id);
}
